import React from "react";
import { FaChevronRight, FaExclamationTriangle, FaPause, FaSyncAlt } from "react-icons/fa";
import "./OcrUploadQueue.css";

const LABELS = { queued: "Waiting", uploading: "Uploading...", reading: "Reading...", ready: "Ready for review",
  processing: "Uploaded; check result", upload_failed: "Upload failed", read_failed: "Reading failed", uncertain: "Upload not confirmed", cancelled: "Cancelled" };

export default function OcrUploadQueue({ queue, t, background = false, onRefresh, refreshDisabled }) {
  const { rows, running, paused, pending } = queue;
  const finished = rows.filter((row) => !["queued", "uploading", "reading"].includes(row.status)).length;
  const uploaded = rows.filter((row) => row.importId).length;
  const failed = rows.filter((row) => ["upload_failed", "read_failed", "uncertain"].includes(row.status)).length;
  const active = rows.find((row) => ["uploading", "reading"].includes(row.status));
  const counts = t("{done} of {total} completed · {uploaded} uploaded · {failed} errors", { done: finished, total: rows.length, uploaded, failed });

  return <div className="ocru-toolbar">
    <button type="button" className="ocrp-btn" disabled={refreshDisabled} onClick={onRefresh}><FaSyncAlt aria-hidden="true" />{t("Refresh")}</button>
    {rows.length > 0 && <details className="ocru-queue" aria-label={t("Upload queue")}>
      <summary title={counts}>
        <FaChevronRight className="ocru-chevron" aria-hidden="true" />
        <span className="ocru-label">{t("Upload queue")}</span>
        <span className="ocru-count" role="status" aria-atomic="true">
          <span aria-hidden="true">{finished}/{rows.length}</span>
          {paused && <FaPause aria-hidden="true" />}
          {failed > 0 && <span className="ocru-warning" aria-hidden="true"><FaExclamationTriangle /> {failed}</span>}
          <span className="ocru-srOnly">{counts} {paused && t("Queue paused.")}</span>
        </span>
        <progress max={rows.length} value={finished} aria-label={t("Upload progress")} />
      </summary>
      <div className="ocru-panel">
        <p>{counts}</p>
        {active && <p className="ocru-current">{t("Current file: {name}", { name: active.name })}</p>}
        {paused && <p>{t(running ? "The queue will pause after the current document." : "Queue paused.")}</p>}
        {queue.busy && <p className="ocru-hint">{t(background
          ? "You can close this inbox and keep working in Purchasing. Uploads and reading continue in the background. Do not reload or close the browser."
          : "Keep this inbox open. Files are uploaded and read one at a time. Cancel pending files to close when the current document finishes.")}</p>}
        {rows.some((row) => row.status === "uncertain") && <p className="ocru-warning">{t("An upload could not be confirmed. Check the inbox before selecting that file again to avoid duplicates.")}</p>}
        <div className="ocru-actions">
          {(running || pending > 0) && <button type="button" className="ocrp-btn" onClick={paused ? queue.resume : queue.pause}>{t(paused ? "Resume queue" : "Pause queue")}</button>}
          {pending > 0 && <button type="button" className="ocrp-btn" onClick={queue.cancelPending}>{t("Cancel pending")}</button>}
          {!running && queue.canRetryFailed && <button type="button" className="ocrp-btn" onClick={queue.retryFailed}>{t("Retry failed")}</button>}
          {!running && rows.some((row) => row.status === "processing") && <button type="button" className="ocrp-btn" onClick={queue.checkPending}>{t("Check pending results")}</button>}
        </div>
        <strong>{t("File results ({count})", { count: rows.length })}</strong>
        <ol className="ocru-results">
          {rows.map((row) => <li key={row.id} data-status={row.status}><div><strong title={row.name}>{row.name}</strong><span>{t(LABELS[row.status])}{row.importId ? ` · #${row.importId}` : ""}</span></div>{row.error && <p>{t(row.error)}</p>}</li>)}
        </ol>
      </div>
    </details>}
  </div>;
}
